import { useState, useEffect } from "react";

const STORAGE_KEY = "disclaimer_accepted";

export default function DisclaimerPopup() {
  const [visible, setVisible] = useState(false);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    const accepted = sessionStorage.getItem(STORAGE_KEY);
    if (!accepted) {
      setVisible(true);
      setTimeout(() => setAnimate(true), 10);
    }
  }, []);

  const handleAgree = () => {
    sessionStorage.setItem(STORAGE_KEY, "true");
    setAnimate(false);
    setTimeout(() => setVisible(false), 300);
  };

  // FIX: was navigating instantly with no transition.
  // Now it mirrors handleAgree — fade/scale out first, THEN navigate.
  const handleDisagree = () => {
    setAnimate(false);
    setTimeout(() => {
      window.location.href = "about:blank";
    }, 300); // matches the 0.3s transition duration of overlay/modal
  };

  if (!visible) return null;

  return (
    <div style={styles.overlay(animate)}>
      <div style={styles.modal(animate)}>

        {/* Header — fixed, never scrolls */}
        <div style={styles.header}>
          <div style={styles.iconWrapper}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
                fill="#c8a84b"
              />
            </svg>
          </div>
          <div>
            <h2 style={styles.title}>DISCLAIMER</h2>
            <p style={styles.subtitle}>R & P Law Partners · Please read carefully</p>
          </div>
        </div>

        <div style={styles.divider} />

        {/* Scrollable body */}
        <div style={styles.body}>
          <p style={styles.text}>
            Under the rules of the Bar Council of India, advocates and law firms are prohibited from soliciting work or advertising in any manner. By accessing this website, the user acknowledges and confirms that:
          </p>

          <p style={styles.boldIntro}>
            The rules of the Bar Council of India prohibit law firms from soliciting work or advertising in any manner.
            By clicking on <strong>'I AGREE'</strong>, the user acknowledges that:
          </p>

          <ul style={styles.list}>
            <li style={styles.listItem}>
              <span style={styles.bullet}>—</span>
              The user wishes to gain information about R & P Law Partners, its practice areas, and its advocates for his/her own information and use.
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>—</span>
              The information made available on this website is provided solely at the specific request of the user. Any material downloaded or information obtained from this website is entirely at the user's discretion and volition.
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>—</span>
              The transmission, receipt, or use of this website does not create any advocate-client relationship between the user and R & P Law Partners.
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>—</span>
              The contents of this website are intended solely for informational purposes and should not be construed as legal advice, legal opinion, or any form of solicitation or advertisement.
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>—</span>
              R & P Law Partners makes no warranties regarding the accuracy, completeness, or adequacy of the information contained on this website. The firm shall not be liable for any consequences arising from any action taken by a user relying upon the material or information provided herein.
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>—</span>
              Users are advised to seek independent legal counsel for any specific legal issues or concerns and should not act solely on the basis of information available on this website.
            </li>
          </ul>

          <p style={styles.subText}>
            By clicking <strong>"I Agree"</strong> button and accessing this website, the user acknowledges that they have read and understood this disclaimer and agree to the terms stated herein.
          </p>
        </div>

        <div style={styles.divider} />

        {/* Buttons — fixed at bottom, never scrolls */}
        <div style={styles.buttonRow}>
          
          <button
            style={styles.btnAgree}
            onClick={handleAgree}
            onMouseEnter={e => (e.currentTarget.style.background = "#a8862e")}
            onMouseLeave={e => (e.currentTarget.style.background = "#c8a84b")}
          >
            I AGREE
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Styles ----------
const styles = {
  overlay: (animate) => ({
    position: "fixed",
    inset: 0,
    background: "rgba(0, 0, 0, 0.75)",
    backdropFilter: "blur(5px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: "20px 16px",
    opacity: animate ? 1 : 0,
    transition: "opacity 0.3s ease",
  }),

  modal: (animate) => ({
    background: "#ffffff",
    borderRadius: "12px",
    maxWidth: "720px",
    width: "100%",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 28px 70px rgba(0,0,0,0.4)",
    overflow: "hidden",
    transform: animate ? "translateY(0) scale(1)" : "translateY(28px) scale(0.97)",
    transition: "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
  }),

  header: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    padding: "22px 32px 18px",
    background: "#fafafa",
    flexShrink: 0,
  },

  iconWrapper: {
    width: "46px",
    height: "46px",
    borderRadius: "50%",
    background: "#fff8e6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    border: "1.5px solid #f0d98a",
  },

  title: {
    margin: 0,
    fontSize: "20px",
    fontWeight: "800",
    letterSpacing: "0.14em",
    color: "#1a1a1a",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    lineHeight: 1.2,
  },

  subtitle: {
    margin: "3px 0 0",
    fontSize: "11px",
    color: "#888",
    letterSpacing: "0.08em",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    textTransform: "uppercase",
  },

  divider: {
    height: "1px",
    background: "#e8e8e8",
    flexShrink: 0,
  },

  body: {
    padding: "24px 32px 20px",
    overflowY: "auto",
    flex: 1,
    scrollbarWidth: "thin",
    scrollbarColor: "#c8a84b #f5f5f5",
  },

  text: {
    margin: "0 0 16px",
    fontSize: "14.5px",
    lineHeight: "1.75",
    color: "#333",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  },

  boldIntro: {
    margin: "0 0 14px",
    fontSize: "14px",
    lineHeight: "1.7",
    color: "#1a1a1a",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    fontWeight: "600",
  },

  list: {
    margin: "0 0 18px",
    padding: 0,
    listStyle: "none",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },

  listItem: {
    display: "flex",
    gap: "10px",
    fontSize: "14px",
    lineHeight: "1.7",
    color: "#444",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  },

  bullet: {
    color: "#c8a84b",
    fontWeight: "700",
    flexShrink: 0,
    marginTop: "1px",
  },

  subText: {
    margin: 0,
    fontSize: "13.5px",
    lineHeight: "1.65",
    color: "#555",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    background: "#fffbf0",
    border: "1px solid #f0d98a",
    borderRadius: "7px",
    padding: "12px 16px",
  },

  buttonRow: {
    display: "flex",
    gap: "12px",
    padding: "18px 32px 22px",
    justifyContent: "flex-end",
    flexShrink: 0,
    background: "#fff",
    borderTop: "1px solid #f0f0f0",
  },

  btnDisagree: {
    padding: "11px 26px",
    borderRadius: "7px",
    border: "1.5px solid #d9534f",
    background: "transparent",
    color: "#d9534f",
    fontWeight: "700",
    fontSize: "13px",
    letterSpacing: "0.08em",
    cursor: "pointer",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    transition: "background 0.15s",
  },

  btnAgree: {
    padding: "11px 30px",
    borderRadius: "7px",
    border: "none",
    background: "#c8a84b",
    color: "#fff",
    fontWeight: "700",
    fontSize: "13px",
    letterSpacing: "0.08em",
    cursor: "pointer",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    transition: "background 0.15s",
    boxShadow: "0 2px 10px rgba(200,168,75,0.4)",
  },
};